import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileJson,
  Image as ImageIcon,
  Save,
  Upload,
  X,
} from "lucide-react";
import { useMenuStore } from "../../../stores/Assets/menu";
import { useMenuItemsStore } from "../../../stores/Assets/menuItems";

const SAMPLE_JSON = {
  menus: [
    {
      name: "Desserts",
      name_ar: "",
      name_fr: "Desserts",
      description: "Sweet items",
      description_ar: "",
      description_fr: "Desserts",
      site_id: 1,
      kitchen_id: 1,
      currency: "QAR",
      currency_ar: "",
      VAT: 0,
      items: [
        {
          name: "Molten Cake",
          name_ar: "",
          name_fr: "Fondant",
          description: "Chocolate cake",
          description_ar: "",
          description_fr: "Gateau chocolat",
          tags: ["Cake", "Chocolate"],
          price: 40,
        },
      ],
    },
  ],
};

const BRAND_COLOR = "#c59253";
const BRAND_LIGHT = "#f0d4b0";
const BRAND_DARK = "#a67840";

const normalizeName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-z0-9]/g, "");

const BulkAddMenusModal = ({
  isOpen,
  toggle,
  selectedSiteId,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [jsonInput, setJsonInput] = useState("");
  const [parseError, setParseError] = useState(null);
  const [validatedMenus, setValidatedMenus] = useState([]);
  const [validMenus, setValidMenus] = useState([]);
  const [summary, setSummary] = useState(null);
  const [menuGroups, setMenuGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { bulkAddMenus } = useMenuStore();
  const { updateItem } = useMenuItemsStore();

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setJsonInput("");
      setParseError(null);
      setValidatedMenus([]);
      setValidMenus([]);
      setSummary(null);
      setMenuGroups([]);
      setSearchTerm("");
    }
  }, [isOpen]);

  const validateAndParse = (text) => {
    if (!text?.trim()) {
      setParseError("Please enter or upload JSON content");
      setValidatedMenus([]);
      setValidMenus([]);
      return;
    }

    try {
      const parsed = JSON.parse(text);
      if (!parsed.menus || !Array.isArray(parsed.menus)) {
        setParseError("JSON must contain a 'menus' array");
        setValidatedMenus([]);
        setValidMenus([]);
        return;
      }

      const selectedSiteNumber =
        selectedSiteId && selectedSiteId !== "All" ? Number(selectedSiteId) : null;

      const validated = parsed.menus.map((menu, menuIndex) => {
        const errors = [];
        const siteId = Number(menu.site_id);
        const kitchenId = Number(menu.kitchen_id);
        const vat = Number(menu.VAT);

        if (!String(menu.name || "").trim()) errors.push("Missing name");
        if (!String(menu.description || "").trim()) errors.push("Missing description");
        if (!siteId) errors.push("Missing site_id");
        if (!kitchenId) errors.push("Missing kitchen_id");
        if (!String(menu.currency || "").trim()) errors.push("Missing currency");
        if (menu.VAT === undefined || Number.isNaN(vat) || vat < 0) {
          errors.push("Invalid VAT");
        }
        if (selectedSiteNumber && siteId !== selectedSiteNumber) {
          errors.push(`site_id must match selected site (${selectedSiteNumber})`);
        }
        if (!Array.isArray(menu.items) || menu.items.length === 0) {
          errors.push("Missing items array");
        }

        const items = Array.isArray(menu.items)
          ? menu.items.map((item, itemIndex) => {
              const itemErrors = [];
              const price = Number(item.price);
              if (!String(item.name || "").trim()) itemErrors.push("Missing name");
              if (item.price === undefined || Number.isNaN(price) || price < 0) {
                itemErrors.push("Invalid price");
              }
              if (item.tags !== undefined && !Array.isArray(item.tags)) {
                itemErrors.push("Tags must be an array");
              }
              return {
                ...item,
                index: itemIndex + 1,
                errors: itemErrors,
              };
            })
          : [];

        return {
          ...menu,
          index: menuIndex + 1,
          errors,
          items,
        };
      });

      setParseError(null);
      setValidatedMenus(validated);
      setValidMenus(
        validated.filter(
          (menu) =>
            menu.errors.length === 0 &&
            menu.items.every((item) => item.errors.length === 0)
        )
      );
    } catch (err) {
      setParseError(`Invalid JSON: ${err.message}`);
      setValidatedMenus([]);
      setValidMenus([]);
    }
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setJsonInput(value);
    validateAndParse(value);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || "";
      setJsonInput(text);
      validateAndParse(text);
    };
    reader.onerror = () => setParseError("Failed to read file");
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  const loadSample = () => {
    const sampleData = {
      ...SAMPLE_JSON,
      menus: SAMPLE_JSON.menus.map((menu) => ({
        ...menu,
        site_id:
          selectedSiteId && selectedSiteId !== "All"
            ? Number(selectedSiteId)
            : menu.site_id,
      })),
    };
    const sample = JSON.stringify(sampleData, null, 2);
    setJsonInput(sample);
    validateAndParse(sample);
  };

  const sanitizeMenus = () =>
    validMenus.map((menu) => ({
      name: String(menu.name).trim(),
      name_ar: String(menu.name_ar || "").trim(),
      name_fr: String(menu.name_fr || "").trim(),
      description: String(menu.description).trim(),
      description_ar: String(menu.description_ar || "").trim(),
      description_fr: String(menu.description_fr || "").trim(),
      site_id: Number(menu.site_id),
      kitchen_id: Number(menu.kitchen_id),
      currency: String(menu.currency).trim(),
      currency_ar: String(menu.currency_ar || "").trim(),
      VAT: Number(menu.VAT),
      items: menu.items.map((item) => ({
        name: String(item.name).trim(),
        name_ar: String(item.name_ar || "").trim(),
        name_fr: String(item.name_fr || "").trim(),
        description: String(item.description || "").trim(),
        description_ar: String(item.description_ar || "").trim(),
        description_fr: String(item.description_fr || "").trim(),
        tags: Array.isArray(item.tags)
          ? item.tags.filter((tag) => String(tag || "").trim()).map((tag) => String(tag).trim())
          : [],
        price: Number(item.price),
      })),
    }));

  const handleCreateMenus = async () => {
    if (validMenus.length === 0) {
      toast.warning("No valid menus to add");
      return;
    }

    try {
      setLoading(true);
      const result = await bulkAddMenus(sanitizeMenus());
      setSummary(result);

      const createdMenus = result?.created_menus || [];
      setMenuGroups(
        createdMenus.map((menu) => ({
          ...menu,
          items: (menu.items || []).map((item) => ({
            ...item,
            item_id: item.menu_item_id,
            stagedImage: null,
            previewUrl: null,
          })),
        }))
      );

      if (result?.inserted_menus > 0) {
        toast.success(`${result.inserted_menus} menus added`);
        setStep(2);
        onSuccess?.();
      } else {
        toast.warning("No menus inserted. Check errors.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to add menus"
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateSimilarity = (itemName, fileName) => {
    const item = normalizeName(itemName);
    const file = normalizeName(fileName);
    let matches = 0;
    const temp = [...item];
    for (const char of file) {
      const index = temp.indexOf(char);
      if (index !== -1) {
        matches++;
        temp.splice(index, 1);
      }
    }
    return matches;
  };

  const handleImportImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const matches = [];
    files.forEach((file, fileIndex) => {
      menuGroups.forEach((menu, menuIndex) => {
        menu.items.forEach((item, itemIndex) => {
          const score = calculateSimilarity(item.title, file.name);
          if (score > 0) {
            matches.push({ file, fileIndex, menuIndex, itemIndex, item, score });
          }
        });
      });
    });

    matches.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (
        Math.abs(normalizeName(a.file.name).length - normalizeName(a.item.title).length) -
        Math.abs(normalizeName(b.file.name).length - normalizeName(b.item.title).length)
      );
    });

    const assignedFiles = new Set();
    const assignedItems = new Set();
    const updated = menuGroups.map((menu) => ({
      ...menu,
      items: menu.items.map((item) => ({ ...item })),
    }));

    matches.forEach((match) => {
      const itemKey = `${match.menuIndex}:${match.itemIndex}`;
      if (!assignedFiles.has(match.fileIndex) && !assignedItems.has(itemKey)) {
        const existingPreview = updated[match.menuIndex].items[match.itemIndex].previewUrl;
        if (existingPreview) URL.revokeObjectURL(existingPreview);
        updated[match.menuIndex].items[match.itemIndex].stagedImage = match.file;
        updated[match.menuIndex].items[match.itemIndex].previewUrl = URL.createObjectURL(match.file);
        assignedFiles.add(match.fileIndex);
        assignedItems.add(itemKey);
      }
    });

    setMenuGroups(updated);
    toast.info(
      assignedItems.size > 0
        ? `${assignedItems.size} images matched`
        : "No matches. Name images like item titles."
    );
    e.target.value = "";
  };

  const handleSingleImageSelect = (menuIndex, itemIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setMenuGroups((prev) =>
      prev.map((menu, mi) =>
        mi !== menuIndex
          ? menu
          : {
              ...menu,
              items: menu.items.map((item, ii) => {
                if (ii !== itemIndex) return item;
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                return {
                  ...item,
                  stagedImage: file,
                  previewUrl: URL.createObjectURL(file),
                };
              }),
            }
      )
    );
    e.target.value = "";
  };

  const handleRemoveStaged = (menuIndex, itemIndex) => {
    setMenuGroups((prev) =>
      prev.map((menu, mi) =>
        mi !== menuIndex
          ? menu
          : {
              ...menu,
              items: menu.items.map((item, ii) => {
                if (ii !== itemIndex) return item;
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                return { ...item, stagedImage: null, previewUrl: null };
              }),
            }
      )
    );
  };

  const handleDragStart = (e, menuIndex, itemIndex) => {
    const item = menuGroups[menuIndex]?.items?.[itemIndex];
    if (!item?.stagedImage) return;
    e.dataTransfer.setData("source", JSON.stringify({ menuIndex, itemIndex }));
  };

  const handleDrop = (e, targetMenuIndex, targetItemIndex) => {
    e.preventDefault();
    const source = e.dataTransfer.getData("source");
    if (!source) return;

    const { menuIndex, itemIndex } = JSON.parse(source);
    if (menuIndex === targetMenuIndex && itemIndex === targetItemIndex) return;

    setMenuGroups((prev) => {
      const updated = prev.map((menu) => ({
        ...menu,
        items: menu.items.map((item) => ({ ...item })),
      }));
      const sourceItem = updated[menuIndex]?.items?.[itemIndex];
      const targetItem = updated[targetMenuIndex]?.items?.[targetItemIndex];
      if (!sourceItem || !targetItem) return prev;

      const sourceImage = sourceItem.stagedImage;
      const sourcePreview = sourceItem.previewUrl;
      sourceItem.stagedImage = targetItem.stagedImage;
      sourceItem.previewUrl = targetItem.previewUrl;
      targetItem.stagedImage = sourceImage;
      targetItem.previewUrl = sourcePreview;
      return updated;
    });
  };

  const stagedCount = useMemo(
    () =>
      menuGroups.reduce(
        (total, menu) => total + menu.items.filter((item) => item.stagedImage).length,
        0
      ),
    [menuGroups]
  );

  const invalidCount = validatedMenus.length - validMenus.length;

  const filteredGroups = menuGroups
    .map((menu) => ({
      ...menu,
      items: menu.items.filter((item) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter((menu) => menu.items.length > 0);

  const handleSaveImages = async () => {
    const itemsToUpdate = menuGroups.flatMap((menu) =>
      menu.items
        .filter((item) => item.stagedImage)
        .map((item) => ({ ...item, menu_id: menu.menu_id }))
    );

    if (!itemsToUpdate.length) {
      toast.warning("No images to save");
      return;
    }

    try {
      setSaving(true);
      for (const item of itemsToUpdate) {
        const formData = new FormData();
        formData.append("newImages", item.stagedImage);
        await updateItem(item.menu_id, item.menu_item_id || item.item_id, formData);
      }
      toast.success("Images saved!");
      onSuccess?.();
      toggle();
    } catch (err) {
      toast.error("Failed to save images");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable centered>
      <ModalHeader toggle={toggle} className="border-0 pb-2 pt-4 px-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 gap-sm-4">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 44, height: 44, background: `${BRAND_LIGHT}80` }}
            >
              <FileJson size={22} color={BRAND_COLOR} />
            </div>
            <div>
              <h5 className="mb-0 fw-semibold" style={{ color: BRAND_DARK }}>
                Bulk Import Menus
              </h5>
              <small className="text-muted">Add menus, items, and optional item images</small>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 gap-md-4 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 34,
                  height: 34,
                  background: step >= 1 ? BRAND_COLOR : "#e9ecef",
                  color: step >= 1 ? "white" : "#6c757d",
                }}
              >
                {step > 1 ? <Check size={18} /> : 1}
              </div>
              <span className="fw-medium d-none d-md-inline">Upload JSON</span>
            </div>
            <div style={{ width: 60, height: 3, background: step >= 2 ? BRAND_COLOR : "#e9ecef" }} />
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 34,
                  height: 34,
                  background: step >= 2 ? BRAND_COLOR : "#e9ecef",
                  color: step >= 2 ? "white" : "#6c757d",
                }}
              >
                2
              </div>
              <span className="fw-medium d-none d-md-inline">Attach Images</span>
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="px-4 pb-4 pt-2">
        {step === 1 ? (
          <Card className="border shadow-sm">
            <CardBody className="p-4 p-md-5">
              <Label className="fw-medium mb-3 d-block">JSON Content</Label>
              <div className="d-flex flex-column flex-md-row gap-4">
                <div className="flex-grow-1">
                  <Input
                    type="textarea"
                    value={jsonInput}
                    onChange={handleJsonChange}
                    placeholder='{"menus": [{ "name": "...", "site_id": 1, "items": [] }]}'
                    rows={16}
                    className="font-monospace bg-dark text-light border-0 shadow-sm"
                    style={{ fontSize: "0.95rem", lineHeight: 1.6, minHeight: 360 }}
                  />
                  {parseError && (
                    <div className="text-danger mt-2 small fw-medium">{parseError}</div>
                  )}
                </div>

                <div className="d-flex flex-row flex-md-column gap-2 align-self-start">
                  <Button
                    style={{ background: BRAND_COLOR, borderColor: BRAND_COLOR }}
                    className="d-flex align-items-center justify-content-center gap-2 px-4 py-3"
                    onClick={() => document.getElementById("bulk-menu-json-file").click()}
                  >
                    <Upload size={18} />
                    Upload File
                  </Button>
                  <input
                    type="file"
                    id="bulk-menu-json-file"
                    accept=".json"
                    hidden
                    onChange={handleFileSelect}
                  />
                  <Button
                    outline
                    style={{ borderColor: BRAND_COLOR, color: BRAND_COLOR }}
                    className="px-4 py-3"
                    onClick={loadSample}
                  >
                    Load Sample
                  </Button>
                </div>
              </div>

              {validatedMenus.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Badge color="primary">{validatedMenus.length} menus parsed</Badge>
                    <Badge color="success">{validMenus.length} valid menus</Badge>
                    <Badge color={invalidCount > 0 ? "danger" : "secondary"}>
                      {invalidCount} invalid menus
                    </Badge>
                  </div>

                  <div className="border rounded overflow-hidden">
                    {validatedMenus.map((menu) => (
                      <div key={`${menu.index}-${menu.name}`} className="p-3 border-bottom">
                        <div className="d-flex justify-content-between gap-3">
                          <div>
                            <div className="fw-semibold">
                              {menu.index}. {menu.name || "Untitled menu"}
                            </div>
                            <small className="text-muted">
                              Site {menu.site_id || "-"} / Kitchen {menu.kitchen_id || "-"} /{" "}
                              {menu.items?.length || 0} items
                            </small>
                          </div>
                          {menu.errors.length === 0 &&
                          menu.items.every((item) => item.errors.length === 0) ? (
                            <Badge color="success">Valid</Badge>
                          ) : (
                            <Badge color="danger">Invalid</Badge>
                          )}
                        </div>
                        {menu.errors.length > 0 && (
                          <div className="text-danger small mt-2">{menu.errors.join(", ")}</div>
                        )}
                        {menu.items.some((item) => item.errors.length > 0) && (
                          <div className="text-danger small mt-2">
                            {menu.items
                              .filter((item) => item.errors.length > 0)
                              .map(
                                (item) =>
                                  `Item ${item.index} (${item.name || "Untitled"}): ${item.errors.join(", ")}`
                              )
                              .join(" | ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {summary?.errors?.length > 0 && (
                <div className="alert alert-warning mt-4 mb-0">
                  {summary.errors.slice(0, 5).map((error, index) => (
                    <div key={`${error.menu}-${error.item}-${index}`}>
                      {error.menu || "Menu"}
                      {error.item ? ` / ${error.item}` : ""}: {error.reason}
                    </div>
                  ))}
                  {summary.errors.length > 5 && <div>And {summary.errors.length - 5} more...</div>}
                </div>
              )}
            </CardBody>
          </Card>
        ) : (
          <Card className="border shadow-sm">
            <CardBody className="p-4">
              <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-4">
                <div>
                  <h5 className="mb-1">Attach Images</h5>
                  <small className="text-muted">
                    Images are grouped by newly created menu. Use filenames close to item names for auto-match.
                  </small>
                </div>
                <div className="d-flex flex-column flex-md-row gap-2">
                  <Input
                    type="text"
                    placeholder="Filter items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button
                    color="primary"
                    className="text-nowrap"
                    onClick={() => document.getElementById("bulk-menu-image-upload").click()}
                  >
                    <Upload size={16} className="me-2" />
                    Import Images
                  </Button>
                  <input
                    type="file"
                    id="bulk-menu-image-upload"
                    multiple
                    accept="image/*"
                    hidden
                    onChange={handleImportImages}
                  />
                </div>
              </div>

              {filteredGroups.length === 0 ? (
                <div className="text-center text-muted py-5">No created items found.</div>
              ) : (
                filteredGroups.map((menu, menuIndex) => {
                  const originalMenuIndex = menuGroups.findIndex((group) => group.menu_id === menu.menu_id);
                  return (
                    <div key={menu.menu_id} className="mb-4">
                      <h6 className="fw-semibold mb-3">{menu.name}</h6>
                      <div className="row g-3">
                        {menu.items.map((item) => {
                          const originalItemIndex = menuGroups[originalMenuIndex].items.findIndex(
                            (sourceItem) => sourceItem.menu_item_id === item.menu_item_id
                          );
                          return (
                            <div
                              key={item.menu_item_id}
                              className="col-12 col-sm-6 col-lg-4"
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => handleDrop(e, originalMenuIndex, originalItemIndex)}
                            >
                              <Card className="h-100 border">
                                <CardBody>
                                  <div className="d-flex gap-3">
                                    <div
                                      draggable={!!item.stagedImage}
                                      onDragStart={(e) =>
                                        handleDragStart(e, originalMenuIndex, originalItemIndex)
                                      }
                                      className="rounded border d-flex align-items-center justify-content-center bg-light"
                                      style={{
                                        width: 86,
                                        height: 86,
                                        overflow: "hidden",
                                        cursor: item.stagedImage ? "grab" : "default",
                                      }}
                                    >
                                      {item.previewUrl ? (
                                        <img
                                          src={item.previewUrl}
                                          alt={item.title}
                                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                      ) : (
                                        <ImageIcon size={28} className="text-muted" />
                                      )}
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <div className="fw-semibold text-truncate">{item.title}</div>
                                      <small className="text-muted">Item #{item.menu_item_id}</small>
                                      {item.stagedImage && (
                                        <small className="d-block text-success text-truncate">
                                          {item.stagedImage.name}
                                        </small>
                                      )}
                                      <div className="d-flex gap-2 mt-3">
                                        <Button
                                          size="sm"
                                          outline
                                          color="primary"
                                          onClick={() =>
                                            document
                                              .getElementById(`menu-item-image-${item.menu_item_id}`)
                                              .click()
                                          }
                                        >
                                          Upload
                                        </Button>
                                        {item.stagedImage && (
                                          <Button
                                            size="sm"
                                            outline
                                            color="danger"
                                            onClick={() =>
                                              handleRemoveStaged(originalMenuIndex, originalItemIndex)
                                            }
                                          >
                                            <X size={14} />
                                          </Button>
                                        )}
                                      </div>
                                      <input
                                        id={`menu-item-image-${item.menu_item_id}`}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) =>
                                          handleSingleImageSelect(originalMenuIndex, originalItemIndex, e)
                                        }
                                      />
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </CardBody>
          </Card>
        )}
      </ModalBody>

      <ModalFooter className="border-0 px-4 pb-4">
        {step === 2 && (
          <Button outline onClick={() => setStep(1)} disabled={saving}>
            <ChevronLeft size={16} className="me-1" />
            Back
          </Button>
        )}
        <Button color="secondary" outline onClick={toggle} disabled={loading || saving}>
          Close
        </Button>
        {step === 1 ? (
          <Button
            style={{ background: BRAND_COLOR, borderColor: BRAND_COLOR }}
            onClick={handleCreateMenus}
            disabled={loading || validMenus.length === 0}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                Create Menus
                <ChevronRight size={16} className="ms-1" />
              </>
            )}
          </Button>
        ) : (
          <Button color="success" onClick={handleSaveImages} disabled={saving || stagedCount === 0}>
            {saving ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Save size={16} className="me-1" />
                Save {stagedCount} Images
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default BulkAddMenusModal;
